// Vendor closures (migration 032, shared/closures.ts): the chip on plan rows a closure moves, the Closures popover on
// both planning pages (every closure, who it covers, vendors with no country), and the vendor panel's own dates.
import { CalendarOff, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { type ClosureDelay, closureDates, closureDays, closuresOf, delayText } from "../../../shared/closures.ts";
import { planToday } from "../../../shared/day.ts";
import type { Vendor, VendorClosure } from "../../../shared/db.gen.ts";
import { shortDate } from "../../../shared/planning.ts";
import { can, mutate } from "../lib/mutate.ts";
import { useRows } from "../lib/sync.ts";
import { CreateDialog } from "../ui/CreateDialog.tsx";
import { Badge, Button } from "../ui/controls.tsx";
import { Popover } from "../ui/Popover.tsx";
import { type FieldDef, Fields, Section } from "../ui/RecordPanel.tsx";
import { PaneLink } from "../ui/SidePane.tsx";

/** "CNY" for Chinese New Year; a short label as it is. */
const shortName = (label: string) => {
  const words = label.trim().split(/\s+/);
  return words.length >= 3 ? words.map((w) => w[0]?.toUpperCase()).join("") : label;
};

/** "CNY +21d" on a plan row whose order-by a closure moved (or, late, whose order lands later). */
export function ClosureChip({ delay, who }: { delay: ClosureDelay | null; who?: string }) {
  if (!delay) return null;
  const name = delay.hit.length === 1 ? shortName(delay.hit[0].label) : "Closed";
  const n = `${delay.days} day${delay.days === 1 ? "" : "s"}`;
  const what = delay.late ? `an order placed now lands ${n} later` : `order-by is ${n} earlier`;
  return (
    <span className="ml-1.5 inline-block align-middle" title={`${delayText(delay)}. ${who || "The vendor"} is closed then, so ${what}.`}>
      <Badge tone="accent">
        {name} +{delay.days}d
      </Badge>
    </span>
  );
}

const vendorName = (v: Pick<Vendor, "name" | "short_name">) => v.short_name || v.name;
const dateText = (v: unknown) => (v ? shortDate(String(v).slice(0, 10), planToday()) : "");
const closureFields: FieldDef[] = [
  { key: "label", label: "Name" },
  { key: "closed_from", label: "Closed from", type: "date", format: dateText },
  { key: "back_on", label: "Back at work", type: "date", format: dateText },
  { key: "notes", label: "Notes", type: "textarea" },
];
const daysText = (c: Pick<VendorClosure, "closed_from" | "back_on">) => {
  const n = closureDays(c);
  return n ? `${closureDates(c)} · ${n} day${n === 1 ? "" : "s"}` : "Doesn't close";
};

/** A new closure, a country's or one vendor's: nothing is saved (or planned with) until it has its name and dates. */
function NewClosure({ vendor, onClose }: { vendor?: Vendor; onClose: () => void }) {
  return (
    <CreateDialog
      title={vendor ? `New closure for ${vendorName(vendor)}` : "New closure for a country's vendors"}
      fields={[
        { key: "label", label: "Name", placeholder: vendor ? "e.g. Factory move" : "e.g. Golden Week" },
        ...(vendor ? [] : [{ key: "country", label: "Country (2 letters)", placeholder: "e.g. CN" }]),
        { key: "closed_from", label: "First day closed", type: "date" as const },
        { key: "back_on", label: "Back at work on", type: "date" as const },
      ]}
      initial={vendor ? {} : { country: "CN" }}
      onClose={onClose}
      onSubmit={(v) =>
        mutate("vendor_closure.create", {
          id: crypto.randomUUID(),
          label: v.label,
          closed_from: v.closed_from,
          back_on: v.back_on,
          ...(vendor ? { vendor_id: vendor.id } : { country: v.country }),
        })
      }
    />
  );
}

/** Every closure on the planning pages: a country's with who it covers and who has their own dates, and vendors' own. */
export function ClosuresButton() {
  const closures = useRows("vendor_closure");
  const vendors = useRows("vendor");
  const editable = can("vendor_closure.update");
  const live = useMemo(() => vendors.filter((v) => !v.archived), [vendors]);
  const today = planToday();
  const upcoming = closures.filter((c) => !c.replaces_id && String(c.back_on) > today).length;
  const [adding, setAdding] = useState(false);
  return (
    <>
      {adding && <NewClosure onClose={() => setAdding(false)} />}
      <Popover
        width={460}
        button={(toggle) => (
          <Button
            variant="ghost"
            icon={<CalendarOff className="size-4" />}
            title="Vendor closures: days vendors don't work, added to lead times"
            onClick={toggle}
          >
            Closures{upcoming ? ` (${upcoming})` : ""}
          </Button>
        )}
      >
        {(close) => (
          <div className="max-h-[70vh] overflow-y-auto">
            <div className="mb-1 flex items-center gap-2">
              <span className="font-medium">Vendor closures</span>
              {can("vendor_closure.create") && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  onClick={() => {
                    close();
                    setAdding(true);
                  }}
                >
                  Add one for a country
                </Button>
              )}
            </div>
            <div className="mb-2 text-[12px] text-muted">
              Days a vendor doesn't work. When they fall inside an order's lead time it takes that much longer, so order-by moves earlier. A vendor's own dates
              are in its panel.
            </div>
            {closures
              .filter((c) => !c.vendor_id)
              .sort((a, b) => String(a.closed_from).localeCompare(String(b.closed_from)))
              .map((c) => {
                const inIt = live.filter((v) => v.country === c.country);
                const own = closures.filter((o) => o.replaces_id === c.id);
                const ownOf = new Map(own.map((o) => [o.vendor_id, o]));
                const same = inIt.filter((v) => !ownOf.has(v.id));
                return (
                  <div key={c.id} className="mb-2 rounded-lg border border-line">
                    <div className="flex items-center gap-2 border-b border-line px-3 py-1.5">
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {c.label} <span className="font-normal text-muted">· every {c.country} vendor</span>
                      </span>
                      <span className="shrink-0 text-[12px] text-muted">{daysText(c)}</span>
                      {can("vendor_closure.remove") && (
                        <button
                          type="button"
                          className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
                          title="Remove"
                          onClick={() =>
                            confirm(`Remove ${c.label}${own.length ? ` and ${own.length} vendor${own.length === 1 ? "'s" : "s'"} own dates for it` : ""}?`) &&
                            mutate("vendor_closure.remove", { id: c.id })
                          }
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="py-1">
                      <Fields compact row={c} fields={closureFields} update="vendor_closure.update" disabled={!editable} />
                    </div>
                    <div className="border-t border-line px-3 py-1.5 text-[12px] text-muted">
                      {same.length
                        ? `${same.length} vendor${same.length === 1 ? "" : "s"}: ${same.map(vendorName).join(", ")}`
                        : inIt.length
                          ? `Every ${c.country} vendor has its own dates:`
                          : `No vendors in ${c.country} yet.`}
                      {own.map((o) => {
                        const v = vendors.find((x) => x.id === o.vendor_id);
                        return (
                          <div key={o.id}>
                            <PaneLink kind="vendor" id={o.vendor_id ?? ""} to={`/vendors?open=${o.vendor_id}`} className="text-accent-text hover:underline">
                              {v ? vendorName(v) : "A vendor"}
                            </PaneLink>
                            : {daysText(o)}
                            {v && v.country !== c.country && <span className="text-faint"> (not in {c.country}: its own dates still apply)</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            <VendorsOwn closures={closures} vendors={vendors} />
            <OtherCountries vendors={live} closures={closures} />
            <NoCountry vendors={live} />
          </div>
        )}
      </Popover>
    </>
  );
}

/** Vendors in countries no closure covers, so one in the wrong country (a Shenzhen factory marked HK) is easy to spot. */
function OtherCountries({ vendors, closures }: { vendors: readonly Vendor[]; closures: readonly VendorClosure[] }) {
  const covered = new Set(closures.filter((c) => !c.vendor_id).map((c) => c.country));
  const byCountry = new Map<string, Vendor[]>();
  for (const v of vendors) if (v.country && !covered.has(v.country)) byCountry.set(v.country, [...(byCountry.get(v.country) ?? []), v]);
  if (!byCountry.size) return null;
  return (
    <div className="mb-2 text-[12px] text-muted">
      <span className="font-medium text-text">No closure for: </span>
      {[...byCountry]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([country, list]) => `${country} (${list.map(vendorName).join(", ")})`)
        .join(" · ")}
      . A vendor in the wrong country? Change it in its panel.
    </div>
  );
}

function VendorsOwn({ closures, vendors }: { closures: readonly VendorClosure[]; vendors: readonly Vendor[] }) {
  const own = closures.filter((c) => c.vendor_id && !c.replaces_id).sort((a, b) => String(a.closed_from).localeCompare(String(b.closed_from)));
  if (!own.length) return null;
  return (
    <div className="mb-2 text-[12px]">
      <div className="mb-1 font-medium">Vendors' own</div>
      {own.map((c) => {
        const v = vendors.find((x) => x.id === c.vendor_id);
        return (
          <div key={c.id} className="flex gap-2">
            <PaneLink kind="vendor" id={c.vendor_id ?? ""} to={`/vendors?open=${c.vendor_id}`} className="text-accent-text hover:underline">
              {v ? vendorName(v) : "A vendor"}
            </PaneLink>
            <span className="text-muted">
              {c.label}: {daysText(c)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Vendors HHL buys from with no country: no country's closure can apply to them. */
function NoCountry({ vendors }: { vendors: readonly Vendor[] }) {
  const links = useRows("vendor_item");
  const buying = useMemo(() => new Set(links.map((l) => l.vendor_id)), [links]);
  const none = vendors.filter((v) => !v.country && buying.has(v.id));
  if (!none.length) return null;
  return (
    <div className="rounded-lg bg-warn-soft px-3 py-2 text-[12px] text-warn">
      {none.length} vendor{none.length === 1 ? " has" : "s have"} items but no country, so no country's closure applies to {none.length === 1 ? "it" : "them"}:{" "}
      {none.slice(0, 8).map(vendorName).join(", ")}
      {none.length > 8 ? ` and ${none.length - 8} more` : ""}. Set Country in the vendor's panel.
    </div>
  );
}

/** The vendor panel's closures: its country's (or its own dates for one) and its own. */
export function VendorClosures({ vendor }: { vendor: Vendor }) {
  const closures = useRows("vendor_closure");
  const editable = can("vendor_closure.update");
  const country = closures.filter((c) => !c.vendor_id && c.country && c.country === vendor.country);
  const ownFor = new Map(closures.filter((c) => c.vendor_id === vendor.id && c.replaces_id).map((c) => [c.replaces_id, c]));
  const own = closures.filter((c) => c.vendor_id === vendor.id && !c.replaces_id);
  // Its own dates for a country's closure it's no longer in (its country changed) still apply.
  const strays = [...ownFor.values()].filter((o) => !country.some((c) => c.id === o.replaces_id));
  const applied = closuresOf(vendor, closures).filter((c) => closureDays(c) > 0).length;
  const [adding, setAdding] = useState(false);
  const change = (c: VendorClosure) =>
    mutate("vendor_closure.create", {
      id: crypto.randomUUID(),
      label: c.label,
      vendor_id: vendor.id,
      replaces_id: c.id,
      closed_from: String(c.closed_from).slice(0, 10),
      back_on: String(c.back_on).slice(0, 10),
    });
  const remove = (c: VendorClosure, what: string) => confirm(what) && mutate("vendor_closure.remove", { id: c.id });
  return (
    <Section title="Closures" count={applied} addLabel="Add closure" onAdd={can("vendor_closure.create") ? () => setAdding(true) : undefined}>
      {adding && <NewClosure vendor={vendor} onClose={() => setAdding(false)} />}
      <div className="px-2 pb-1 text-[12px] text-muted">
        {vendor.country
          ? `Days it doesn't work: added to an order's lead time when they fall inside it.`
          : strays.length + own.length
            ? "No country set, so no country's closure applies: only its own, below. Set Country above for its country's."
            : "No country set, so no country's closure (like Chinese New Year for CN) applies. Set Country above."}
      </div>
      {country.map((c) => {
        const mine = ownFor.get(c.id);
        return (
          <div key={c.id} className="mx-2 mb-2 rounded-lg border border-line">
            <div className="flex items-center gap-2 border-b border-line px-3 py-1.5">
              <span className="min-w-0 flex-1 truncate font-medium">
                {c.label} <span className="font-normal text-muted">· {mine ? "its own dates" : `every ${c.country} vendor`}</span>
              </span>
              {!mine && editable && (
                <Button size="sm" variant="ghost" onClick={() => change(c)}>
                  Change for this vendor
                </Button>
              )}
              {mine && can("vendor_closure.remove") && (
                <Button size="sm" variant="ghost" onClick={() => remove(mine, `Use ${c.country}'s dates for ${c.label} again (${daysText(c)})?`)}>
                  Use {c.country}'s dates
                </Button>
              )}
            </div>
            <div className="py-1">
              {mine ? (
                <Fields compact row={mine} fields={closureFields.filter((f) => f.key !== "label")} update="vendor_closure.update" disabled={!editable} />
              ) : (
                <div className="px-3 py-1 text-[12px] text-muted">{daysText(c)}</div>
              )}
              {mine && (
                <div className="px-3 pb-1 text-[11px] text-faint">
                  Same day twice: it doesn't close. {c.country}'s dates: {daysText(c)}.
                </div>
              )}
            </div>
          </div>
        );
      })}
      {[...strays, ...own].map((c) => (
        <div key={c.id} className="mx-2 mb-2 rounded-lg border border-line">
          <div className="flex items-center gap-2 border-b border-line px-3 py-1.5">
            <span className="min-w-0 flex-1 truncate font-medium">
              {c.replaces_id ? (closures.find((p) => p.id === c.replaces_id)?.label ?? c.label) : c.label}
            </span>
            <span className="shrink-0 text-[12px] text-muted">{daysText(c)}</span>
            {can("vendor_closure.remove") && (
              <button
                type="button"
                className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
                title="Remove"
                onClick={() => remove(c, `Remove ${c.label}?`)}
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
          <div className="py-1">
            <Fields
              compact
              row={c}
              fields={c.replaces_id ? closureFields.filter((f) => f.key !== "label") : closureFields}
              update="vendor_closure.update"
              disabled={!editable}
            />
          </div>
        </div>
      ))}
    </Section>
  );
}
